import { TermDto } from 'generated';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentTermDTO } from 'redux/configuration/selector';
import { SelectOptionType } from 'utils/types';
import { getUrlParam } from 'utils/utils';
import useAllTermsGet from './useAllTermsGet';

interface IProps {
  terms?: TermDto[];
  defaultSelected?: SelectOptionType | undefined;
}

const useGroupTermsBySessions = ({ terms, defaultSelected }: IProps) => {
  const urlTermId = getUrlParam('term');
  const { terms: allTerms, loading } = useAllTermsGet({});

  const urlTerm = allTerms.find((term) => term.term_id === urlTermId);

  const currentTermDTO: TermDto = useSelector(selectCurrentTermDTO);
  const termList = terms ? terms : allTerms;

  const [selectedTerm, setSelectedTerm] = useState<SelectOptionType>(
    defaultSelected || {
      label: `${currentTermDTO?.school_term_definition?.name}`,
      value: currentTermDTO,
    },
  );

  const [sessionValue, setSessionValue] = useState<SelectOptionType>(
    !!defaultSelected
      ? {
          label: defaultSelected?.value.session?.id || '',
          value: defaultSelected?.value,
        }
      : {
          label: currentTermDTO?.session?.id || '',
          value: currentTermDTO,
        },
  );

  useEffect(() => {
    setSessionValue(
      defaultSelected
        ? {
            label: defaultSelected?.value.session?.id || '',
            value: defaultSelected?.value,
          }
        : {
            label: currentTermDTO?.session?.id || '',
            value: currentTermDTO,
          },
    );
    setSelectedTerm(
      defaultSelected || {
        label: `${currentTermDTO?.school_term_definition?.name}`,
        value: currentTermDTO,
      },
    );
  }, [defaultSelected, currentTermDTO]);

  const [selectTermOptions, setSelectTermOptions] = useState<any>([]);

  useEffect(() => {
    if (urlTerm) {
      setSelectedTerm({
        label: `${urlTerm?.school_term_definition?.name}`,
        value: urlTerm,
      });
      setSessionValue({
        label: `${urlTerm?.session?.id}`,
        value: urlTerm,
      });
    }

    const session = urlTerm ? urlTerm.session?.id : currentTermDTO?.session?.id;
    const list = termList?.filter((term) => term.session?.id === session);
    setSelectTermOptions(list);
  }, [currentTermDTO?.session?.id, termList, urlTerm]);

  const groupedTermsBySessions = useMemo(() => {
    const grouped: Record<string, TermDto[]> = {};

    termList.forEach((item) => {
      if (item?.session) {
        if (!grouped[item.session!.id!]) {
          grouped[item.session!.id!] = [item];
        } else {
          grouped[item.session!.id!] = [...grouped[item.session!.id!], item]?.sort(
            (a, b) =>
              (a?.school_term_definition?.definition_order || 0) -
              (b?.school_term_definition?.definition_order || 0),
          );
        }
      }
    });
    return grouped;
  }, [termList]);

  return {
    groupedTermsBySessions,
    selectedTerm,
    setSelectedTerm,
    selectTermOptions,
    setSelectTermOptions,
    sessionValue,
    setSessionValue,
    allTerms,
    loading,
    currentTermDTO,
  };
};

export default useGroupTermsBySessions;
